/**
 * Collection NG Cards
 *
 * Block-level renderer for the "NG (API) card" mode of the collection blocks
 * (_grid-collection / _carousel-collection). Given a collection's MA mapping
 * keys (node id / search term, read from collection metafields in Liquid), it
 * queries the APIM product-search and renders ng-product-card markup.
 *
 * Because the data is async, the cards can't be server-rendered as direct
 * children of the grid / swiper-wrapper. This element is a placeholder that,
 * once resolved, injects the finished cards as siblings (so they become direct
 * grid items / slides) and removes itself. For a carousel it then re-inits the
 * parent <carousel-component> so Swiper counts the new slides.
 *
 * Config via data-attributes:
 *   data-layout      "grid" | "carousel"
 *   data-node-id     MA node id (collection.metafields.custom.filter_node_id)
 *   data-search-term MA search term (collection.metafields.custom.search_term)
 *   data-max         max products to render
 *   data-item-class  (grid) class string for each product's column wrapper
 *   data-item-style  (grid) inline style (CSS vars) for each column wrapper
 */

import { queryProductList } from '@theme/query-product-list';
import { trackProductSearch } from '@theme/hydrate-callers';
import { renderProductCard, renderProductCardSkeleton } from '@theme/ng-product-card';

class CollectionNgCards extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.hydrated) return;
    this.dataset.hydrated = 'true';
    try {
      await this.#render();
    } catch {
      this.#empty('Couldn’t load collection products.');
    }
  }

  /**
   * No products to show. In the theme editor keep a visible, selectable
   * placeholder (the APIM fetch usually returns nothing in the editor preview,
   * same as hydrate-plp-products); on the live storefront just remove.
   * @param {string} message
   */
  #empty(message) {
    this.#clearSkeletons();
    if (window.Shopify && window.Shopify.designMode) {
      this.style.gridColumn = '1 / -1';
      this.innerHTML = `<p class="tw:m-0 tw:p-6 tw:text-center tw:text-sm tw:text-gray-500">${message}</p>`;
      return;
    }
    this.remove();
  }

  async #render() {
    const nodeId = (this.dataset.nodeId || '').trim();
    const searchTerm = (this.dataset.searchTerm || '').trim();
    const max = parseInt(this.dataset.max || '12', 10) || 12;
    const layout = this.dataset.layout === 'carousel' ? 'carousel' : 'grid';

    // No mapping on the collection → nothing to query.
    if (!nodeId && !searchTerm) {
      this.#empty('This collection has no NG mapping (filter_node_id / search_term metafield).');
      return;
    }

    const params = { query: searchTerm, size: max };
    if (nodeId) params.filters = { 'node.id': [nodeId] };

    // Grid: paint skeleton cells while the API call is in flight so the row
    // doesn't sit empty. (Carousel is left alone — injecting skeleton slides
    // would need a Swiper re-init just to swap them out again.)
    if (layout === 'grid') this.#showSkeletons(max);

    const res = await queryProductList(params);

    // This block runs a real NG product search off the collection's metafield
    // mapping, so it reports like any other — otherwise an API-card collection
    // renders products that no search event accounts for.
    trackProductSearch(searchTerm, 'collection-cards', res);
    const products = res && res.ok && res.data && Array.isArray(res.data.products)
      ? res.data.products.slice(0, max)
      : [];

    if (!products.length) {
      this.#empty('No NG products to display (they load from the API on the live storefront).');
      return;
    }

    const html = products.map(hit => this.#wrap(renderProductCard(hit), layout)).join('');
    const parent = this.parentElement;

    // Swap the skeletons out and inject the finished cards as siblings so they
    // become direct children of the grid / swiper-wrapper, then drop this
    // placeholder.
    this.#clearSkeletons();
    this.insertAdjacentHTML('beforebegin', html);
    this.remove();

    // Slides were added after the carousel initialised — re-init so Swiper
    // counts them (initSwiper destroys + recreates the instance).
    if (layout === 'carousel' && parent) {
      const carousel = parent.closest('carousel-component');
      if (carousel && typeof carousel.initSwiper === 'function') carousel.initSwiper();
    }
  }

  /**
   * Injects `count` grid skeleton cells as siblings before this placeholder,
   * each wrapped in the same column markup as a real card so the row lays out
   * identically while loading. Each wrapper is tagged `data-ng-skeleton` so
   * #clearSkeletons can remove them once the data resolves.
   * @param {number} count
   */
  #showSkeletons(count) {
    const itemClass = this.dataset.itemClass || '';
    const itemStyle = this.dataset.itemStyle || '';
    const cell = `<div class="${itemClass} tw:grid" style="${itemStyle}" data-ng-skeleton aria-hidden="true">${renderProductCardSkeleton()}</div>`;
    this.insertAdjacentHTML('beforebegin', cell.repeat(Math.min(Math.max(count, 1), 12)));
  }

  /** Removes any skeleton cells injected by #showSkeletons. */
  #clearSkeletons() {
    const parent = this.parentElement;
    if (parent) parent.querySelectorAll(':scope > [data-ng-skeleton]').forEach((n) => n.remove());
  }

  /**
   * Wrap a rendered ng-product-card in the layout's item markup so it lays out
   * identically to the Liquid-card mode.
   * @param {string} cardHtml
   * @param {'grid'|'carousel'} layout
   * @returns {string}
   */
  #wrap(cardHtml, layout) {
    // `tw:grid` on the immediate wrapper makes the single card fill the cell in
    // both axes, so it stretches to the (equal-height) column / slide — matching
    // how the PLP grid's <li> cards stretch as direct grid items.
    if (layout === 'carousel') {
      return `<div class="swiper-slide tw:h-auto!"><div class="carousel-slide-content tw:grid tw:w-full tw:h-full">${cardHtml}</div></div>`;
    }
    const itemClass = this.dataset.itemClass || '';
    const itemStyle = this.dataset.itemStyle || '';
    return `<div class="${itemClass} tw:grid" style="${itemStyle}">${cardHtml}</div>`;
  }
}

if (!customElements.get('collection-ng-cards')) {
  customElements.define('collection-ng-cards', CollectionNgCards);
}
